import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Mail,
  RefreshCw,
  Store,
  BookOpen,
  Settings,
  Bot,
  Inbox,
  Send,
  FileText,
  Archive,
  Trash2,
  ShieldAlert,
  FolderTree,
  AlertCircle,
  ChevronDown,
  GripVertical,
  PanelLeft,
  PanelLeftClose,
  Maximize2,
  Minimize2,
  Star,
  CheckCircle2,
} from 'lucide-react';
import type { ElementType } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inboxEmailApi, type ImapFolderNode, type ImapFoldersTree } from '../../lib/api';
import { Dropdown } from '../ui/Dropdown';
import { FolderTreePanel, type FolderTreeClickTarget } from './FolderTreePanel';
import { FolderContextMenu } from './FolderContextMenu';
import { FolderCreateModal } from './FolderCreateModal';
import { FavoriteFolderModal, resolveFolderIcon } from './FavoriteFolderModal';
import { ConfirmDialog } from '../ConfirmDialog';

/* ------------------------------------------------------------------------ */
/* Typy                                                                     */
/* ------------------------------------------------------------------------ */

interface StoreItem {
  id: string;
  name: string;
  messageInboxPosition: number;
  unreadCount: number;
  mailboxCount: number;
}

interface FolderCounts {
  inbox?: number | null;
  starred?: number | null;
  sent?: number | null;
  trash?: number | null;
  drafts?: number | null;
  archive?: number | null;
  spam?: number | null;
  [key: string]: number | null | undefined;
}

interface InboxLabelItem {
  id: string;
  name: string;
  color: string;
  position: number;
  _count?: { messages: number };
}

export interface InboxSidebarSelection {
  /** Wirtualny widok: inbox/resolved/starred/sent/drafts/archive/trash/spam/review_queue. */
  virtual: string | null;
  /** Konkretny folder IMAP z drzewa (imapFolderId). */
  imapFolderId: string | null;
}

interface InboxSidebarProps {
  collapsed?: boolean;
  stores: StoreItem[];
  storesLoadFailed?: boolean;
  selectedStoreId: string;
  onStoreChange: (storeId: string) => void;
  onStoresReorder: (storeIds: string[]) => void;
  /** Aktualna selekcja (wirtualna lub konkretny folder). */
  selection: InboxSidebarSelection;
  /** Callback na zmianę selekcji. */
  onSelectionChange: (next: InboxSidebarSelection) => void;
  folderCounts: FolderCounts | null;
  activeLabelFilter: string | null;
  onLabelFilterChange: (labelId: string | null) => void;
  labels: InboxLabelItem[];
  onNewMessage: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  reviewQueueCount?: number;
  resolvedCount?: number;
  /** Konfiguracja plakietek — per grupa sidebara. Default: wszystkie true. */
  badgeDisplay?: {
    inbox: boolean;
    imapFolders: boolean;
    favorites: boolean;
  };
  /** Toggle zwinięcia sidebara. Jeśli podany — renderuje sekcję "Zwiń menu" na dole. */
  onToggle?: () => void;
  /** Toggle trybu fullscreen. Jeśli podany — w bottom panelu pojawia się
   * 50/50 split (Zwiń | Ekran) zamiast samego Zwiń. W fullscreen sam
   * przycisk wyjścia (Minimize2). */
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
}

/* ------------------------------------------------------------------------ */
/* System folders layout                                                    */
/* ------------------------------------------------------------------------ */

interface SystemFolderDef {
  key: string;
  label: string;
  icon: ElementType;
  specialUse?: string;
  tooltip?: string;
}

const SYSTEM_FOLDERS: SystemFolderDef[] = [
  { key: 'sent', label: 'Wysłane', icon: Send, specialUse: '\\Sent', tooltip: 'Wiadomości wysłane z serwera pocztowego' },
  { key: 'drafts', label: 'Szkice', icon: FileText, specialUse: '\\Drafts' },
  { key: 'archive', label: 'Archiwum', icon: Archive, specialUse: '\\Archive' },
  { key: 'trash', label: 'Kosz', icon: Trash2, specialUse: '\\Trash' },
  { key: 'spam', label: 'Spam', icon: ShieldAlert, specialUse: '\\Junk' },
];

const FAVORITES_SOFT_LIMIT = 15;

function MailboxStoreSelect({
  stores,
  selectedStoreId,
  storesLoadFailed,
  onStoreChange,
  onStoresReorder,
}: {
  stores: StoreItem[];
  selectedStoreId: string;
  storesLoadFailed: boolean;
  onStoreChange: (storeId: string) => void;
  onStoresReorder: (storeIds: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draggedStoreId, setDraggedStoreId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedStore = stores.find(store => store.id === selectedStoreId);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const gap = 6;
      const desiredHeight = Math.min(320, stores.length * 43 + 44);
      const availableBelow = window.innerHeight - rect.bottom - gap - 8;
      const availableAbove = rect.top - gap - 8;
      const opensAbove = availableBelow < Math.min(desiredHeight, 180) && availableAbove > availableBelow;
      const maxHeight = Math.max(120, Math.min(desiredHeight, opensAbove ? availableAbove : availableBelow));
      setMenuPosition({
        top: opensAbove ? Math.max(8, rect.top - gap - maxHeight) : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, stores.length]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const moveStore = (storeId: string, direction: -1 | 1) => {
    const fromIndex = stores.findIndex(store => store.id === storeId);
    const toIndex = fromIndex + direction;
    if (fromIndex < 0 || toIndex < 0 || toIndex >= stores.length) return;
    const reordered = [...stores];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onStoresReorder(reordered.map(store => store.id));
  };

  const dropStoreBefore = (targetStoreId: string) => {
    if (!draggedStoreId || draggedStoreId === targetStoreId) return;
    const reordered = [...stores];
    const fromIndex = reordered.findIndex(store => store.id === draggedStoreId);
    const targetIndex = reordered.findIndex(store => store.id === targetStoreId);
    if (fromIndex < 0 || targetIndex < 0) return;
    const [moved] = reordered.splice(fromIndex, 1);
    const adjustedTarget = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
    reordered.splice(adjustedTarget, 0, moved);
    onStoresReorder(reordered.map(store => store.id));
  };

  return (
    <div className="px-1">
      <p className="px-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-text-disabled">Sklep</p>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(current => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={`flex w-full items-center gap-2 rounded-input border bg-surface-base/60 px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 ${
          open ? 'border-accent-primary/50' : 'border-border hover:border-border-strong'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Store className={`h-3.5 w-3.5 shrink-0 ${selectedStore ? 'text-accent-primary' : 'text-text-muted'}`} />
        <span className={`min-w-0 flex-1 truncate text-xs ${selectedStore ? 'font-medium text-text-primary' : 'text-text-muted'}`}>
          {selectedStore?.name || 'Wybierz sklep'}
        </span>
        {selectedStore && (
          <span className={`min-w-[22px] rounded-md px-1.5 py-0.5 text-center text-[10px] tabular-nums ${
            selectedStore.unreadCount > 0 ? 'bg-accent-primary/15 text-accent-primary' : 'text-text-disabled'
          }`}>
            {selectedStore.unreadCount}
          </span>
        )}
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && menuPosition && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Sklepy w Wiadomościach"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
            zIndex: 11050,
          }}
          className="overflow-y-auto rounded-xl border border-border bg-surface-raised p-1.5 shadow-glass backdrop-blur-xl animate-scale-in"
        >
          <div className="flex items-center justify-between px-2 pb-1.5 pt-0.5">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-text-disabled">Sklepy</span>
            <span className="text-[9px] tabular-nums text-text-disabled">{stores.length}</span>
          </div>
          {stores.map((store) => {
            const isSelected = store.id === selectedStoreId;
            return (
              <div
                key={store.id}
                role="option"
                aria-selected={isSelected}
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = 'move';
                  setDraggedStoreId(store.id);
                }}
                onDragEnd={() => setDraggedStoreId(null)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => dropStoreBefore(store.id)}
                className={`flex items-center rounded-lg border transition-colors ${
                  isSelected
                    ? 'border-accent-primary/25 bg-accent-primary/10'
                    : 'border-transparent hover:border-border-subtle hover:bg-surface-hover/50'
                } ${draggedStoreId === store.id ? 'opacity-50' : ''}`}
              >
                <GripVertical className="ml-1 h-3.5 w-3.5 shrink-0 cursor-grab text-text-disabled" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => {
                    onStoreChange(store.id);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  onKeyDown={(event) => {
                    if (!event.altKey) return;
                    if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      moveStore(store.id, -1);
                    } else if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      moveStore(store.id, 1);
                    }
                  }}
                  className="flex min-w-0 flex-1 items-center gap-2 px-1.5 py-2 text-left"
                  title="Alt + strzałka zmienia kolejność"
                >
                  <Store className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-accent-primary' : 'text-text-muted'}`} />
                  <span className={`min-w-0 flex-1 truncate text-xs ${isSelected ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
                    {store.name}
                  </span>
                  <span className={`min-w-[22px] rounded-md px-1.5 py-0.5 text-center text-[10px] tabular-nums ${
                    store.unreadCount > 0 ? 'bg-accent-primary/15 text-accent-primary' : 'text-text-disabled'
                  }`}>
                    {store.unreadCount}
                  </span>
                </button>
              </div>
            );
          })}
          {storesLoadFailed && stores.length === 0 && (
            <p className="px-2 py-2 text-xs text-danger">Nie udało się wczytać sklepów. Kliknij Odśwież.</p>
          )}
          {!storesLoadFailed && stores.length === 0 && (
            <p className="px-2 py-2 text-xs text-text-muted">Brak skonfigurowanych sklepów</p>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Komponent                                                                */
/* ------------------------------------------------------------------------ */

export function InboxSidebar({
  collapsed = false,
  stores,
  storesLoadFailed = false,
  selectedStoreId,
  onStoreChange,
  onStoresReorder,
  selection,
  onSelectionChange,
  folderCounts,
  activeLabelFilter,
  onLabelFilterChange,
  labels,
  onNewMessage,
  onRefresh,
  isRefreshing = false,
  reviewQueueCount,
  resolvedCount,
  badgeDisplay = { inbox: true, imapFolders: true, favorites: true },
  onToggle,
  onFullscreenToggle,
  isFullscreen = false,
}: InboxSidebarProps) {
  const queryClient = useQueryClient();

  // Pełne drzewo folderów IMAP per config.
  const { data: treeData } = useQuery<ImapFoldersTree>({
    queryKey: ['imap-folders-tree', selectedStoreId],
    queryFn: async () => {
      const res = await inboxEmailApi.getFoldersTree(selectedStoreId);
      return res.data;
    },
    enabled: Boolean(selectedStoreId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const configs = treeData?.data ?? [];

  // Zebranie wszystkich folderów z wszystkich configów — spłaszczenie do lookupu.
  const allFolders = useMemo(() => {
    const result: Array<{ configId: string; folder: ImapFolderNode }> = [];
    const walk = (configId: string, node: ImapFolderNode) => {
      result.push({ configId, folder: node });
      node.children.forEach(c => walk(configId, c));
    };
    configs.forEach(cfg => cfg.folders.forEach(f => walk(cfg.id, f)));
    return result;
  }, [configs]);

  const favorites = useMemo(
    () =>
      allFolders
        .filter(({ folder }) => folder.isFavorite)
        .sort((a, b) => (a.folder.favoriteOrder ?? 999) - (b.folder.favoriteOrder ?? 999)),
    [allFolders],
  );

  // Lookup configId dla dowolnego folder.id — używany w context menu (który config
  // powinien być użyty do createSubfolder / rename).
  const folderToConfig = useMemo(() => {
    const map = new Map<string, string>();
    allFolders.forEach(({ configId, folder }) => map.set(folder.id, configId));
    return map;
  }, [allFolders]);


  /* ---------------- Context menu & modale ---------------- */
  const [contextMenu, setContextMenu] = useState<{
    target: FolderTreeClickTarget;
    anchor: { x: number; y: number };
  } | null>(null);
  const [createModal, setCreateModal] = useState<{
    configId: string;
    accountName: string;
    mode: 'create' | 'rename';
    initialPath?: string;
    parentFolder?: ImapFolderNode | null;
    folderId?: string;
  } | null>(null);
  const [favoriteModal, setFavoriteModal] = useState<{
    folderId: string;
    name: string;
    color: string | null;
    icon: string | null;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ folderId: string; path: string } | null>(null);
  const [deletePending, setDeletePending] = useState(false);

  const invalidateTree = () => queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'] });

  /* ---------------- Akcje z context menu ---------------- */

  const handleFavoriteToggle = async (target: FolderTreeClickTarget) => {
    const { folder } = target;
    if (folder.isFavorite) {
      await inboxEmailApi.setFolderFavorite(folder.id, { isFavorite: false });
      invalidateTree();
    } else {
      // Otwórz modal do wyboru koloru/ikony — zapis dzieje się w onSubmit modalu.
      setFavoriteModal({
        folderId: folder.id,
        name: folder.name,
        color: folder.color,
        icon: folder.icon,
      });
    }
  };

  const handleCreateSubfolder = (target: FolderTreeClickTarget) => {
    const config = configs.find(c => c.id === target.configId);
    setCreateModal({
      configId: target.configId,
      accountName: config?.name || '',
      mode: 'create',
      parentFolder: target.folder,
    });
  };

  const handleRename = (target: FolderTreeClickTarget) => {
    const config = configs.find(c => c.id === target.configId);
    setCreateModal({
      configId: target.configId,
      accountName: config?.name || '',
      mode: 'rename',
      initialPath: target.folder.path,
      folderId: target.folder.id,
    });
  };

  const handleDelete = (target: FolderTreeClickTarget) => {
    setDeleteConfirm({ folderId: target.folder.id, path: target.folder.path });
  };

  /* ---------------- Renderers ---------------- */

  const isVirtualActive = (key: string) =>
    selection.virtual === key && !selection.imapFolderId && !activeLabelFilter;
  const isImapFolderActive = (id: string) =>
    selection.imapFolderId === id && !activeLabelFilter;

  const renderVirtualButton = (
    key: string,
    label: string,
    Icon: ElementType,
    count: number | null,
    tooltip?: string,
  ) => {
    const isActive = isVirtualActive(key);
    return (
      <button
        key={key}
        onClick={() => onSelectionChange({ virtual: key, imapFolderId: null })}
        title={collapsed ? `${label}${count ? ` (${count})` : ''}` : tooltip}
        className={`flex items-center w-full rounded-lg text-sm transition-all ${
          isActive
            ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent hover:border-border-subtle'
        } ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5'}`}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-accent-primary' : ''}`} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left truncate">{label}</span>
            {count != null && count > 0 && (
              <span
                className={`text-[11px] min-w-[20px] text-center px-1.5 py-0.5 rounded-md ${
                  isActive ? 'bg-accent-primary/20 text-accent-primary' : 'bg-surface-hover/80 text-text-muted'
                }`}
              >
                {count}
              </span>
            )}
          </>
        )}
      </button>
    );
  };

  const renderImapFolderButton = (
    key: string,
    label: string,
    Icon: ElementType,
    folder: ImapFolderNode,
    iconColor?: string,
    overrideCount?: number | null,
    hideBadge?: boolean,
  ) => {
    const isActive = isImapFolderActive(folder.id);
    const displayCount = hideBadge ? null : (overrideCount ?? folder.unreadCount);
    return (
      <button
        key={key}
        onClick={() =>
          onSelectionChange({ virtual: null, imapFolderId: folder.id })
        }
        onContextMenu={(e) => {
          e.preventDefault();
          // Foldery systemowe (specialUse set) nie mają menu — są już w głównym menu,
          // nie można ich rename/delete/usunąć z ulubionych (bo nie są w ulubionych).
          if (folder.specialUse) return;
          const configId = folderToConfig.get(folder.id) ?? configs[0]?.id ?? '';
          setContextMenu({
            target: { configId, folder },
            anchor: { x: e.clientX, y: e.clientY },
          });
        }}
        title={collapsed ? `${label}${displayCount ? ` (${displayCount})` : ''}` : folder.path}
        className={`flex items-center w-full rounded-lg text-sm transition-all ${
          isActive
            ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent hover:border-border-subtle'
        } ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5'}`}
      >
        <Icon
          className="w-4 h-4 flex-shrink-0"
          style={iconColor ? { color: iconColor } : undefined}
        />
        {!collapsed && (
          <>
            <span className="flex-1 text-left truncate">{label}</span>
            {displayCount != null && displayCount > 0 && (
              <span
                className={`text-[11px] min-w-[20px] text-center px-1.5 py-0.5 rounded-md tabular-nums ${
                  isActive ? 'bg-accent-primary/20 text-accent-primary' : 'bg-surface-hover/80 text-text-muted'
                }`}
              >
                {displayCount}
              </span>
            )}
          </>
        )}
      </button>
    );
  };

  // Resolve "system" folder (Drafts/Sent/Archive/Trash/Junk) — bierzemy first match
  // po specialUse z pierwszego configa (unified view). Jeśli user ma wiele kont, w v1
  // aggregujemy licznikami z folderCounts (stats endpoint) ale klik idzie do pierwszego.
  const resolveSystemFolder = (specialUse: string): ImapFolderNode | null => {
    for (const cfg of configs) {
      const find = (nodes: ImapFolderNode[]): ImapFolderNode | null => {
        for (const n of nodes) {
          if (n.specialUse === specialUse) return n;
          const sub = find(n.children);
          if (sub) return sub;
        }
        return null;
      };
      const match = find(cfg.folders);
      if (match) return match;
    }
    return null;
  };

  // Sumuj unreadCount z WSZYSTKICH kont dla danego specialUse (spójne z drzewem IMAP).
  const getSystemFolderCount = (specialUse: string): number => {
    let total = 0;
    for (const cfg of configs) {
      const find = (nodes: ImapFolderNode[]): void => {
        for (const n of nodes) {
          if (n.specialUse === specialUse) { total += n.unreadCount; return; }
          find(n.children);
        }
      };
      find(cfg.folders);
    }
    return total;
  };

  const [allFoldersOpen, setAllFoldersOpen] = useState(false);

  /* ---------------- Efekty ---------------- */

  // Zamknij menu kontekstowe przy zmianie selekcji.
  useEffect(() => {
    if (contextMenu) setContextMenu(null);
  }, [selection.imapFolderId, selection.virtual]);

  /* ---------------- Render ---------------- */

  return (
    <div className="w-full h-full flex-shrink-0 border-r border-border-subtle flex flex-col overflow-hidden bg-surface-raised/20">
      {/* Top bar */}
      <div
        className={`flex items-center border-b border-border-subtle flex-shrink-0 h-[52px] ${
          collapsed ? 'px-1 justify-center gap-1.5' : 'px-3 gap-3'
        }`}
      >
        {collapsed ? (
          <button
            onClick={onNewMessage}
            className="btn-primary p-2 flex items-center justify-center !rounded-[8px]"
            title="Nowa wiadomość"
          >
            <Mail className="w-3.5 h-3.5" />
          </button>
        ) : (
          <>
            <button
              onClick={onNewMessage}
              className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-xs py-2 !rounded-[8px]"
            >
              <Mail className="w-3.5 h-3.5" />
              Wiadomość
            </button>
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="btn-ghost p-2 disabled:opacity-60"
              title={isRefreshing ? 'Synchronizacja w toku…' : 'Odśwież'}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </>
        )}
      </div>

      <div className={`flex-1 overflow-y-auto p-2 space-y-3 ${collapsed ? 'px-1' : ''}`}>
        {/* Sklep: kompaktowy select, a w rozwinięciu pełna lista z licznikami i zmianą kolejności. */}
        {!collapsed ? (
          <MailboxStoreSelect
            stores={stores}
            selectedStoreId={selectedStoreId}
            storesLoadFailed={storesLoadFailed}
            onStoreChange={onStoreChange}
            onStoresReorder={onStoresReorder}
          />
        ) : (
          /* #R7-5 / #R8-3 - collapsed sidebar: klik na ikonkę Store otwiera
              dropdown POD spodem (placement='bottom', default). User skorygował
              wcześniejsze "po prawej" - bottom jest naturalniejszy.
              `!block w-full` na wrapperze Dropdown żeby ikona była wycentrowana
              (default `inline-block` powodował przesunięcie w lewo).
              `portal` żeby menu nie było obcinane przez overflow-hidden
              outer InboxSidebara - menu rendererowane do document.body z
              z-[1000], pozycja liczona z getBoundingClientRect triggera. */
          <Dropdown
            className="!block w-full"
            menuFullWidth={false}
            portal
            trigger={
              <button
                className="flex items-center justify-center w-full p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-all"
                title={stores.find(s => s.id === selectedStoreId)?.name || 'Wybierz sklep'}
              >
                <Store className="w-4 h-4" />
              </button>
            }
            items={stores.map(s => ({
              id: s.id,
              label: s.name,
              icon: <Store />,
              onClick: () => onStoreChange(s.id),
            }))}
          />
        )}

        {/* Sekcja 1: Inbox (unified + per-account) */}
        <div className="space-y-0.5">
          {!collapsed && (
            <p className="text-[9px] font-semibold text-text-disabled uppercase tracking-wider px-2 pb-0.5">Odebrane</p>
          )}
          <button
            onClick={() => onSelectionChange({ virtual: 'review_queue', imapFolderId: null })}
            title={collapsed ? `Odpowiedzi AI (${reviewQueueCount ?? 0})` : undefined}
            className={`flex items-center w-full rounded-lg text-sm transition-all ${
              isVirtualActive('review_queue')
                ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent hover:border-border-subtle'
            } ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5'}`}
          >
            <Bot
              className={`w-4 h-4 flex-shrink-0 ${
                isVirtualActive('review_queue') ? 'text-accent-primary' : 'text-info'
              }`}
            />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Odpowiedzi AI</span>
                <span
                  className={`text-[11px] min-w-[20px] text-center px-1.5 py-0.5 rounded-md ${
                    isVirtualActive('review_queue')
                      ? 'bg-accent-primary/20 text-accent-primary'
                      : 'bg-info-bg text-info'
                  }`}
                >
                  {reviewQueueCount ?? 0}
                </span>
              </>
            )}
          </button>
          {renderVirtualButton('inbox', 'Odebrane', Inbox, badgeDisplay.inbox ? (folderCounts?.inbox ?? null) : null)}
          {renderVirtualButton('resolved', 'Rozwiązane', CheckCircle2, resolvedCount ?? null)}
          {renderVirtualButton('starred', 'Wyróżnione', Star, folderCounts?.starred ?? null)}
        </div>

        {/* Sekcja 2: System folders (special-use) */}
        <div className="space-y-0.5">
          {!collapsed && (
            <p className="text-[9px] font-semibold text-text-disabled uppercase tracking-wider px-2 pb-0.5">System</p>
          )}
          {SYSTEM_FOLDERS.map(f => {
            const count = badgeDisplay.imapFolders ? (folderCounts?.[f.key] ?? null) : null;
            // Drafts i Trash: ZAWSZE virtual view. Lokalne szkice oraz świeżo
            // soft-deleted wiadomości nie muszą jeszcze należeć do docelowego
            // folderu IMAP. Backend łączy stan lokalny ze stanem serwera.
            if (f.key === 'drafts' || f.key === 'trash') {
              return renderVirtualButton(f.key, f.label, f.icon, count, f.tooltip);
            }
            const resolved = f.specialUse ? resolveSystemFolder(f.specialUse) : null;
            if (resolved) {
              // Sumuj unreadCount z WSZYSTKICH kont (spójne z drzewem IMAP).
              const countAcrossAccounts = badgeDisplay.imapFolders ? getSystemFolderCount(f.specialUse!) : 0;
              return renderImapFolderButton(f.key, f.label, f.icon, resolved, undefined, countAcrossAccounts || null, !badgeDisplay.imapFolders);
            }
            return renderVirtualButton(f.key, f.label, f.icon, count, f.tooltip);
          })}
        </div>

        {/* Sekcja 3: Ulubione */}
        {favorites.length > 0 && (
          <div className="space-y-0.5">
            {!collapsed && (
              <p className="text-[9px] font-semibold text-text-disabled uppercase tracking-wider px-2 pb-0.5">Ulubione</p>
            )}
            {favorites.map(({ folder }) => {
              const Icon = resolveFolderIcon(folder.icon);
              return renderImapFolderButton(
                `fav-${folder.id}`,
                folder.name,
                Icon,
                folder,
                folder.color || undefined,
                undefined,
                !badgeDisplay.favorites,
              );
            })}
            {!collapsed && favorites.length >= FAVORITES_SOFT_LIMIT && (
              <div className="text-[10px] text-warning/80 px-3 py-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                Sidebar robi się długi — rozważ usunięcie rzadziej używanych
              </div>
            )}
          </div>
        )}

        {/* Sekcja 4: Wszystkie foldery (collapsible) */}
        {!collapsed && configs.length > 0 && (
          <div className="space-y-0.5">
            <button
              onClick={() => setAllFoldersOpen(v => !v)}
              className="flex items-center gap-2 w-full rounded-lg px-2 py-1.5 text-[10px] font-semibold text-text-disabled uppercase tracking-wider hover:text-text-primary hover:bg-surface-hover/30"
            >
              <ChevronDown
                className={`w-3 h-3 transition-transform ${allFoldersOpen ? '' : '-rotate-90'}`}
              />
              <FolderTree className="w-3 h-3" />
              <span className="flex-1 text-left">Wszystkie foldery ({allFolders.length})</span>
            </button>
            {allFoldersOpen && (
              <FolderTreePanel
                configs={configs}
                activeFolderId={selection.imapFolderId}
                showBadges={badgeDisplay.imapFolders}
                onFolderClick={({ folder }) =>
                  onSelectionChange({ virtual: null, imapFolderId: folder.id })
                }
                onContextMenu={(target, anchor) => setContextMenu({ target, anchor })}
                onCreateFolder={(configId, parentFolder) => {
                  const cfg = configs.find(c => c.id === configId);
                  setCreateModal({
                    configId,
                    accountName: cfg?.name || '',
                    mode: 'create',
                    parentFolder,
                  });
                }}
                onFavoriteToggle={(target) => handleFavoriteToggle(target)}
                className="max-h-[400px] bg-surface-raised/40 rounded-lg border border-border-subtle"
              />
            )}
          </div>
        )}

        {/* Labels */}
        {labels.length > 0 && (
          <div className="space-y-0.5">
            {!collapsed && (
              <p className="text-[9px] font-semibold text-text-disabled uppercase tracking-wider px-2 pb-0.5">Etykiety</p>
            )}
            {labels.map(label => {
              const isActive = activeLabelFilter === label.id;
              return (
                <button
                  key={label.id}
                  onClick={() => onLabelFilterChange(isActive ? null : label.id)}
                  title={
                    collapsed
                      ? `${label.name}${label._count?.messages ? ` (${label._count.messages})` : ''}`
                      : undefined
                  }
                  className={`flex items-center w-full rounded-lg text-xs transition-all ${
                    isActive
                      ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 border border-transparent hover:border-border-subtle'
                  } ${collapsed ? 'justify-center px-2 py-2' : 'gap-2.5 px-3 py-2'}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: label.color }}
                  />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left truncate">{label.name}</span>
                      {label._count?.messages != null && label._count.messages > 0 && (
                        <span className="text-[10px] text-text-muted">{label._count.messages}</span>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="flex-shrink-0 border-t border-border p-2 space-y-1.5">
        <Link
          to="/messages/knowledge-base"
          title={collapsed ? 'Baza wiedzy' : undefined}
          className={`flex items-center rounded-lg text-sm text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all ${
            collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          {!collapsed && 'Baza wiedzy'}
        </Link>
        <Link
          to="/messages/settings"
          title={collapsed ? 'Ustawienia' : undefined}
          className={`flex items-center rounded-lg text-sm text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all ${
            collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5'
          }`}
        >
          <Settings className="w-4 h-4" />
          {!collapsed && 'Ustawienia'}
        </Link>
      </div>

      {/* Toggle zwijania + fullscreen (analogicznie jak Layout sidebar bottom).
          W fullscreen sam przycisk wyjścia. Inaczej 50/50 split (rozwinięte)
          albo dwie ikonki w pionie (zwinięte). */}
      {(onToggle || onFullscreenToggle) && (
        <div className={`flex-shrink-0 border-t border-border py-2 ${collapsed ? 'px-1.5' : 'px-2'}`}>
          {isFullscreen && onFullscreenToggle ? (
            <button
              onClick={onFullscreenToggle}
              className="flex items-center justify-center w-full px-3 py-2.5 rounded-lg text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
              title="Wyjdź z trybu pełnoekranowego (Cmd+Shift+F)"
            >
              <Minimize2 className="w-[15px] h-[15px] flex-shrink-0" />
            </button>
          ) : collapsed && onFullscreenToggle ? (
            <div className="flex flex-col gap-1">
              {onToggle && (
                <button
                  onClick={onToggle}
                  className="flex items-center justify-center px-2 py-2 rounded-lg text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                  title="Rozwiń menu"
                >
                  <PanelLeft className="w-[15px] h-[15px]" />
                </button>
              )}
              <button
                onClick={onFullscreenToggle}
                className="flex items-center justify-center px-2 py-2 rounded-lg text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                title="Tryb pełnoekranowy (Cmd+Shift+F)"
              >
                <Maximize2 className="w-[15px] h-[15px]" />
              </button>
            </div>
          ) : !collapsed && onFullscreenToggle && onToggle ? (
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={onToggle}
                className="flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-[11px] uppercase tracking-wider text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                title="Zwiń menu"
              >
                <PanelLeftClose className="w-[15px] h-[15px] flex-shrink-0" />
                <span>Zwiń</span>
              </button>
              <button
                onClick={onFullscreenToggle}
                className="flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-[11px] uppercase tracking-wider text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                title="Tryb pełnoekranowy (Cmd+Shift+F)"
              >
                <Maximize2 className="w-[15px] h-[15px] flex-shrink-0" />
                <span>Ekran</span>
              </button>
            </div>
          ) : onToggle ? (
            <button
              onClick={onToggle}
              className={`flex items-center px-3 py-2.5 rounded-lg text-[11px] uppercase tracking-wider text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 w-full cursor-pointer ${
                collapsed ? 'justify-center !px-2' : 'gap-[10px]'
              }`}
              title={collapsed ? 'Rozwiń menu' : 'Zwiń menu'}
            >
              {collapsed ? (
                <PanelLeft className="w-[15px] h-[15px] flex-shrink-0" />
              ) : (
                <PanelLeftClose className="w-[15px] h-[15px] flex-shrink-0" />
              )}
              {!collapsed && <span>Zwiń menu</span>}
            </button>
          ) : null}
        </div>
      )}

      {/* Context menu (prawy klik na folderze) */}
      {contextMenu && (
        <FolderContextMenu
          anchor={contextMenu.anchor}
          isFavorite={contextMenu.target.folder.isFavorite}
          isSystemFolder={!!contextMenu.target.folder.specialUse}
          onFavoriteToggle={() => handleFavoriteToggle(contextMenu.target)}
          onRename={() => handleRename(contextMenu.target)}
          onCreateSubfolder={() => handleCreateSubfolder(contextMenu.target)}
          onDelete={() => handleDelete(contextMenu.target)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Modal: tworzenie / rename foldera */}
      {createModal && (
        <FolderCreateModal
          isOpen
          mode={createModal.mode}
          accountName={createModal.accountName}
          initialPath={createModal.initialPath}
          parentFolder={createModal.parentFolder}
          onSubmit={async (path) => {
            if (createModal.mode === 'create') {
              const delimiter = createModal.parentFolder?.delimiter || '/';
              await inboxEmailApi.createImapFolder(createModal.configId, path, delimiter);
            } else if (createModal.folderId) {
              await inboxEmailApi.renameImapFolder(createModal.folderId, path);
            }
            invalidateTree();
          }}
          onClose={() => setCreateModal(null)}
        />
      )}

      {/* Modal: wygląd ulubionego (kolor + ikona) */}
      {favoriteModal && (
        <FavoriteFolderModal
          isOpen
          folderName={favoriteModal.name}
          initialColor={favoriteModal.color}
          initialIcon={favoriteModal.icon}
          onSubmit={async ({ color, icon }) => {
            const nextOrder = favorites.length; // append na koniec
            await inboxEmailApi.setFolderFavorite(favoriteModal.folderId, {
              isFavorite: true,
              favoriteOrder: nextOrder,
              color,
              icon,
            });
            invalidateTree();
          }}
          onClose={() => setFavoriteModal(null)}
        />
      )}

      {/* Confirm: usunięcie foldera na serwerze IMAP (destructive) */}
      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title="Usunąć folder z serwera IMAP?"
        message={
          <>
            Folder <strong>{deleteConfirm?.path}</strong> zostanie usunięty z Twojej skrzynki pocztowej
            (Gmail/Outlook/inne) razem ze wszystkimi wiadomościami w nim.
          </>
        }
        description="Operacja jest nieodwracalna — będzie widoczna również w webmailu."
        confirmLabel="Usuń folder"
        isPending={deletePending}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={async () => {
          if (!deleteConfirm) return;
          setDeletePending(true);
          try {
            await inboxEmailApi.deleteImapFolder(deleteConfirm.folderId);
            invalidateTree();
            setDeleteConfirm(null);
          } finally {
            setDeletePending(false);
          }
        }}
      />

    </div>
  );
}
